if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.variantMediaMap = this.parseVariantMediaMap();
        this.variantMediaSyncEnabled = this.dataset.variantMediaSync === 'true';

        if (this.variantMediaSyncEnabled) {
          this.elements.viewer?.querySelectorAll('[data-media-id]').forEach((mediaItem) => {
            mediaItem.addEventListener('click', () => {
              this.setActiveMedia(mediaItem.dataset.mediaId, false);
            });
          });
        }

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      parseVariantMediaMap() {
        const script = this.querySelector('[data-variant-media-map]');
        if (!script) return {};
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error('Failed to parse variant media map', error);
          return {};
        }
      }

      /**
       * Pick the best variant for a media item: prefer one that keeps current
       * option values when several variants share the same image.
       */
      resolveVariantEntry(mediaNumericId) {
        const entries = this.variantMediaMap[mediaNumericId];
        if (!entries?.length) return null;

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        const currentOptionIds = variantSelects?.selectedOptionValues?.map(String) || [];

        const scored = entries.map((entry) => {
          const optionIds = (entry.optionValueIds || []).map(String);
          const overlap = optionIds.filter((id) => currentOptionIds.includes(id)).length;
          return {
            entry,
            overlap,
            availableScore: entry.available ? 1 : 0,
          };
        });

        scored.sort((a, b) => {
          if (b.overlap !== a.overlap) return b.overlap - a.overlap;
          return b.availableScore - a.availableScore;
        });

        return scored[0].entry;
      }

      selectVariantForMedia(mediaId) {
        if (!this.variantMediaSyncEnabled || !mediaId) return;

        const mediaNumericId = String(mediaId).includes('-')
          ? String(mediaId).slice(String(mediaId).lastIndexOf('-') + 1)
          : String(mediaId);

        const entry = this.resolveVariantEntry(mediaNumericId);
        if (!entry?.optionValueIds?.length) return;

        const variantSelects = this.closest('product-info')?.querySelector('variant-selects');
        if (!variantSelects || typeof variantSelects.selectOptionValues !== 'function') return;

        variantSelects.selectOptionValues(entry.optionValueIds.map(String));
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }

        // prepend=true means variant-driven (Dawn). Stable gallery keeps DOM order fixed.
        const fromVariantChange = Boolean(prepend);
        const shouldPrepend = fromVariantChange && !this.variantMediaSyncEnabled;
        const alreadyActive = activeMedia.classList.contains('is-active');

        // Already on this media: skip UI; only sync variant on user-initiated clicks.
        if (alreadyActive) {
          if (!fromVariantChange) this.selectVariantForMedia(mediaId);
          return;
        }

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');

        if (shouldPrepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        } else if (!fromVariantChange) {
          // User-initiated gallery navigation → select variant (once)
          this.selectVariantForMedia(mediaId);
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
